/**
 * Dynamically resizes and compresses an uploaded image file so that high-resolution
 * or large-pixel images (e.g. 4K, large uncompressed PNG/JPEG) are scaled down to fit
 * within reasonable boundaries and memory footprints before upload.
 *
 * @param {File} file - The uploaded image file
 * @param {Object} options - Configuration options
 * @param {number} options.maxWidth - Maximum allowed width (default: 1920)
 * @param {number} options.maxHeight - Maximum allowed height (default: 1920)
 * @param {number} options.quality - Compression quality between 0 and 1 (default: 0.85)
 * @returns {Promise<File>} The resized and compressed File (or original file if not applicable)
 */
export const compressAndResizeImage = (file, options = {}) => {
  return new Promise((resolve) => {
    if (!file || !file.type || !file.type.startsWith('image/')) {
      return resolve(file);
    }

    // Preserve animated GIFs without flattening frames
    if (file.type === 'image/gif') {
      return resolve(file);
    }

    const {
      maxWidth = 1920,
      maxHeight = 1920,
      quality = 0.85
    } = options;

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      let { width, height } = img;

      const needsResize = width > maxWidth || height > maxHeight;
      const isLargeFile = file.size > 1024 * 1024; // > 1MB

      // If dimensions are already within limits and file is small, keep original
      if (!needsResize && !isLargeFile) {
        return resolve(file);
      }

      // Calculate proportional dimensions maintaining aspect ratio
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }
      if (height > maxHeight) {
        width = Math.round((width * maxHeight) / height);
        height = maxHeight;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return resolve(file);
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      // Use JPEG for large compressed images, PNG if original was PNG and small
      const outputType = file.type === 'image/png' && !isLargeFile ? 'image/png' : 'image/jpeg';

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            return resolve(file);
          }

          // If blob somehow turned out larger than original without resizing, use original
          if (blob.size >= file.size && !needsResize) {
            return resolve(file);
          }

          const fileExtension = outputType === 'image/jpeg' ? '.jpg' : '.png';
          const newFileName = file.name.replace(/\.[^/.]+$/, "") + fileExtension;

          const compressedFile = new File(
            [blob],
            newFileName,
            {
              type: outputType,
              lastModified: Date.now(),
            }
          );

          resolve(compressedFile);
        },
        outputType,
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };

    img.src = objectUrl;
  });
};
